import { useRef, useEffect, useState } from "react";

/* ============================================================
   HERO + OrbitalSystem centerpiece (data-viz)
   ============================================================ */

type Mouse = { x: number; y: number; on: boolean };
type DrawFn = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  t: number,
  mouse: Mouse
) => void;

/* ---------- shared canvas sizing hook ---------- */
function useCanvas(draw: DrawFn, deps?: React.DependencyList) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Respect reduced-motion: paint a single static frame, no rAF loop.
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0,
      w = 0,
      h = 0;
    const mouse: Mouse = { x: 0.5, y: 0.4, on: false };
    const t0 = performance.now();
    function size() {
      const r = canvas!.getBoundingClientRect();
      w = r.width;
      h = r.height;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    function onMove(e: PointerEvent) {
      const r = canvas!.getBoundingClientRect();
      mouse.x = (e.clientX - r.left) / r.width;
      mouse.y = (e.clientY - r.top) / r.height;
      mouse.on = true;
    }
    function onLeave() {
      mouse.on = false;
    }
    function loop(now: number) {
      draw(ctx!, w, h, (now - t0) / 1000, mouse);
      raf = requestAnimationFrame(loop);
    }
    size();
    window.addEventListener("resize", size);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerleave", onLeave);
    if (reduced) {
      draw(ctx!, w, h, 0, mouse); // one static frame only
    } else {
      loop(performance.now()); // synchronous first paint, then self-schedules
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", size);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerleave", onLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps || []);
  return ref;
}

/* ============== A · ORBITAL SYSTEM ============== */
function OrbitalSystem() {
  const orbits = [
    { r: 0.16, s: 0.55, c: "#cf7044", sz: 4.5, tilt: 0.42, label: "MARS · M-1142" },
    { r: 0.27, s: -0.34, c: "#9aa0ad", sz: 3.2, tilt: 0.42, label: "MOON · L-0731" },
    { r: 0.4, s: 0.22, c: "#7fb2ff", sz: 5, tilt: 0.42, label: "" },
    { r: 0.53, s: -0.15, c: "#ffd9a0", sz: 3.6, tilt: 0.42, label: "" },
    { r: 0.68, s: 0.1, c: "#9affd0", sz: 4, tilt: 0.42, label: "AST · 1807 FA" },
  ];
  const ref = useCanvas((ctx, w, h, t) => {
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2,
      cy = h / 2,
      R = Math.min(w, h) * 0.5;
    // core glow
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.22);
    g.addColorStop(0, "rgba(180,225,255,0.9)");
    g.addColorStop(0.4, "rgba(120,180,255,0.35)");
    g.addColorStop(1, "rgba(120,180,255,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, R * 0.22, 0, 6.283);
    ctx.fill();
    ctx.fillStyle = "rgba(220,240,255,0.95)";
    ctx.beginPath();
    ctx.arc(cx, cy, 4.5, 0, 6.283);
    ctx.fill();
    // orbits + planets
    orbits.forEach((o, i) => {
      const rx = o.r * R,
        ry = o.r * R * o.tilt;
      ctx.strokeStyle = "rgba(150,180,235,0.16)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, 6.283);
      ctx.stroke();
      const a = t * o.s + i * 1.7;
      const px = cx + Math.cos(a) * rx,
        py = cy + Math.sin(a) * ry;
      // connector
      if (o.label) {
        ctx.strokeStyle = "rgba(150,180,235,0.22)";
        ctx.setLineDash([2, 4]);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(px, py);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      const pg = ctx.createRadialGradient(px, py, 0, px, py, o.sz * 3);
      pg.addColorStop(0, o.c);
      pg.addColorStop(1, "transparent");
      ctx.fillStyle = pg;
      ctx.beginPath();
      ctx.arc(px, py, o.sz * 3, 0, 6.283);
      ctx.fill();
      ctx.fillStyle = o.c;
      ctx.beginPath();
      ctx.arc(px, py, o.sz, 0, 6.283);
      ctx.fill();
      if (o.label) {
        ctx.font = "600 9.5px 'JetBrains Mono', monospace";
        ctx.fillStyle = "rgba(200,220,255,0.78)";
        ctx.fillText(o.label, px + 9, py + 3);
        ctx.strokeStyle = "rgba(150,180,235,0.4)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(px + 5, py);
        ctx.lineTo(px + 7, py);
        ctx.stroke();
      }
    });
  });
  return <canvas ref={ref} className="cp-canvas" />;
}

/* ---------- count-up ---------- */
function Stat({
  to,
  label,
  suffix = "",
  decimals = 0,
}: {
  to: number;
  label: string;
  suffix?: string;
  decimals?: number;
}) {
  const [v, setV] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setV(to); // show the final value immediately, no count-up
      return;
    }
    let done = false;
    const io = new IntersectionObserver(
      (es) => {
        es.forEach((e) => {
          if (e.isIntersecting && !done) {
            done = true;
            const dur = 1500,
              t0 = performance.now();
            (function tick(now: number) {
              const p = Math.min(1, (now - t0) / dur);
              const e2 = 1 - Math.pow(1 - p, 3);
              setV(to * e2);
              if (p < 1) requestAnimationFrame(tick);
            })(t0);
          }
        });
      },
      { threshold: 0.4 }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [to]);
  return (
    <div className="stat" ref={ref}>
      <div className="stat-v tnum">
        {v.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}
        {suffix}
      </div>
      <div className="stat-l mono">{label}</div>
    </div>
  );
}

export default function Hero({ onClaim }: { onClaim?: () => void }) {
  return (
    <header className="hero" data-screen-label="Hero">
      {/* Instrument HUD disabled per design tweak (hud: off) */}
      <div className="wrap hero-grid">
        <div className="hero-copy">
          <span className="eyebrow" data-reveal>
            COSMIC MARKETPLACE FOR REAL ASSETS
          </span>
          <h1 className="h-xl hero-h" data-reveal>
            OWN A<br />
            VERIFIED
            <br />
            <span className="grad">
              PIECE OF THE
              <br />
              UNIVERSE
            </span>
          </h1>
          <p className="lead hero-lead" data-reveal>
            NiftySky tokenizes real astronomical bodies — planets, moons, stars and minor planets —
            indexed from <strong>NASA, IAU &amp; USGS</strong> data and recorded forever on-chain as
            verifiable deeds.
          </p>
          <div className="hero-cta" data-reveal>
            <a
              className="btn btn-primary"
              href="/real-estate/mars"
              onClick={(e) => {
                if (onClaim) {
                  e.preventDefault();
                  onClaim();
                }
              }}
            >
              CLAIM A PIECE OF MARS <span className="ar">→</span>
            </a>
            <a className="btn btn-ghost" href="#registries">
              EXPLORE THE REGISTRY
            </a>
          </div>
          <div className="hero-prov mono" data-reveal>
            <span>BACKED BY</span>
            <b>NASA</b>
            <i>·</i>
            <b>IAU</b>
            <i>·</i>
            <b>USGS</b>
          </div>
        </div>
        <div className="hero-cp" data-reveal>
          <div className="cp-frame ticks">
            <div className="cp-frame-tag mono">
              <span>ORBITAL SURVEY</span>
              <span className="cp-frame-live">
                <span className="dot dot-live"></span>LIVE
              </span>
            </div>
            <OrbitalSystem />
            <div className="cp-frame-foot mono">
              <span>FEED · NASA VIZ</span>
              <span>SOL SYSTEM</span>
            </div>
          </div>
        </div>
      </div>
      <div className="wrap hero-stats" data-reveal>
        <Stat to={10841} label="REGIONS INDEXED" />
        <span className="stat-div"></span>
        <Stat to={2.3} suffix="K" decimals={1} label="DEEDS MINTED" />
        <span className="stat-div"></span>
        <Stat to={1749} label="REGISTERED OWNERS" />
        <span className="stat-div"></span>
        <Stat to={100} suffix="%" label="ON-CHAIN VERIFIED" />
      </div>
      <a className="hero-scroll mono" href="#mission">
        SCROLL ▾ INITIATE SURVEY
      </a>
    </header>
  );
}
